import { t, Trans } from '@lingui/macro'
import { Chart, ChartModel, ChartModelParams } from 'components/Charts/ChartModel'
import { useHeaderDateFormatter } from 'components/Charts/hooks'
import Column from 'components/Column'
import Row from 'components/Row'
import { TimePeriod } from 'graphql/data/util'
import { useActiveLocale } from 'hooks/useActiveLocale'
import { useScreenSize } from 'hooks/useScreenSize'
import { ISeriesApi } from 'lightweight-charts'
import { useCallback, useEffect, useMemo, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { colors } from 'theme/colors'
import { ThemedText } from 'theme/components'
import { Z_INDEX } from 'theme/zIndex'
import { NumberType, useFormatter } from 'utils/formatNumbers'

import TimePeriodSelector from '../TimeSelector'
import { CrosshairHighlightPrimitive } from '../VolumeChart/CrosshairHighlightPrimitive'
import { StackedBarsData } from './renderer'
import { getCumulativeSum, StackedBarsSeries } from './stacked-bar-series'

const TIME_SELECTOR_OPTIONS = [
  { time: TimePeriod.DAY, display: 'D' },
  { time: TimePeriod.WEEK, display: 'W' },
  { time: TimePeriod.MONTH, display: 'M' },
]

const ChartContainer = styled.div<{ $height: number }>`
  width: 100%;
  min-width: 292px;
  height: ${({ $height }) => $height}px;
  position: relative;
  padding-bottom: 12px;

  @media only screen and (max-width: ${({ theme }) => `${theme.breakpoint.sm}px`}) {
    background-color: ${({ theme }) => theme.surface2};
    border-radius: 20px;
    min-width: auto;
    height: 120px;
    padding: 20px;
  }

  @media only screen and (max-width: ${({ theme }) => `${theme.breakpoint.xs}px`}) {
    height: 112px;
  }
`

const ChartHeader = styled.div`
  display: flex;
  flex-direction: column;
  position: absolute;
  padding-top: 12px;
  padding-bottom: 14px;
  gap: 8px;
  text-align: left;
  pointer-events: none;

  @media only screen and (max-width: ${({ theme }) => `${theme.breakpoint.sm}px`}) {
    padding: 0px;
    gap: 4px;
  }
`

const TimeSelectorContainer = styled.div`
  position: absolute;
  top: 8px;
  right: 0px;
  z-index: ${Z_INDEX.active};
`

const StyledTimePeriodSelector = styled(TimePeriodSelector)`
  & > button {
    padding: 4px 8px;
    margin: 4px 0px;
    font-size: 14px;
  }
`

const Legend = styled(Column)`
  position: absolute;
  top: 48px;
  right: 12px;
  padding-top: 4px;
  padding-bottom: 4px;
  gap: 12px;
  text-align: left;
  pointer-events: none;
`

const ProtocolLegend = styled(Row)`
  justify-content: flex-end;
  gap: 8px;
`

const LegendColoredSquare = styled.div<{ $color: string }>`
  width: 12px;
  height: 12px;
  background-color: ${({ $color }) => $color};
  border-radius: 4px;
`

type StackedVolumeChartModelParams = ChartModelParams<StackedBarsData> & { colors: [string, string] }

class StackedVolumeChartModel extends ChartModel<StackedBarsData> {
  protected series: ISeriesApi<'Custom'>

  constructor(chartDiv: HTMLDivElement, params: StackedVolumeChartModelParams) {
    super(chartDiv, params)

    this.series = this.api.addCustomSeries(new StackedBarsSeries({ colors: params.colors }))

    this.series.setData(this.data)
    this.updateOptions(params)
    this.fitContent()
  }

  updateOptions(params: StackedVolumeChartModelParams) {
    const stackedVolumeChartOptions = {
      localization: {
        locale: params.locale,
      },
      rightPriceScale: {
        visible: false,
      },
      handleScale: {
        axisPressedMouseMove: false,
      },
      handleScroll: {
        vertTouchDrag: false,
      },
      grid: {
        vertLines: {
          visible: false,
        },
        horzLines: {
          visible: false,
        },
      },
      crosshair: {
        horzLine: {
          visible: false,
          labelVisible: false,
        },
        vertLine: {
          visible: false,
          labelVisible: false,
        },
      },
    }

    super.updateOptions(params, stackedVolumeChartOptions)
    const { data, theme } = params

    // Handles changes in data, e.g. time period selection
    if (this.data !== data) {
      this.data = data
      this.series.setData(data)
      this.fitContent()
    }

    this.series.applyOptions({
      priceFormat: {
        type: 'volume',
      },
      priceLineVisible: false,
      lastValueVisible: false,
    })

    this.series.priceScale().applyOptions({
      scaleMargins: {
        top: 0.465,
        bottom: 0,
      },
    })

    // Add crosshair highlight bar
    const crosshair = new CrosshairHighlightPrimitive({
      color: theme.surface3,
      crosshairYPosition: 125,
    })
    this.series.attachPrimitive(crosshair)
  }
}

const HeaderValueText = styled(ThemedText.HeadlineLarge)`
  && {
    @media only screen and (max-width: ${({ theme }) => `${theme.breakpoint.sm}px`}) {
      font-size: 24px;
      line-height: 32px;
    }
  }
`

function StackedVolumeChartHeader({ headerData }: { headerData: { time: string; value: string } }) {
  return (
    <ChartHeader>
      <ThemedText.SubHeader color="neutral2">
        <Trans>Uniswap volume</Trans>
      </ThemedText.SubHeader>
      <HeaderValueText>{headerData.value}</HeaderValueText>
      <ThemedText.Caption color="neutral2">{headerData.time}</ThemedText.Caption>
    </ChartHeader>
  )
}

export function StackedVolumeChart({ height }: { height: number }) {
  const locale = useActiveLocale()
  const theme = useTheme()
  const format = useFormatter()
  const screenSize = useScreenSize()
  const isSmallScreen = !screenSize['sm']
  const headerDateFormatter = useHeaderDateFormatter()

  const [headerData, setHeaderData] = useState<{ time: string; value: string }>({ time: '-', value: '-' })
  const [v3Volume, setV3Volume] = useState<string>('-')
  const [v2Volume, setV2Volume] = useState<string>('-')

  const { formatFiatPrice } = useFormatter()
  const priceFormatter = useCallback(
    (price?: number) =>
      formatFiatPrice({
        price,
        type: NumberType.FiatTokenStatChartHeader,
      }),
    [formatFiatPrice]
  )

  const [timePeriod, setTimePeriod] = useState<TimePeriod>(TimePeriod.DAY)
  // Mock refresh of data when timePeriod changes
  const data = useMemo(() => {
    // note: timePeriod on the Explore Page represents the GRANULARITY, not the timespan of data shown.
    // i.e. timePeriod == D shows 1month data, timePeriod == W shows 1year data, timePeriod == M shows alltime data
    let numDataPoints = 0
    switch (timePeriod) {
      case TimePeriod.DAY:
        numDataPoints = 30
        break
      case TimePeriod.WEEK:
        numDataPoints = 52
        break
      case TimePeriod.MONTH:
        numDataPoints = 36
        break
    }
    const now = Date.now() / 1000
    const mockData = []
    for (let i = 0; i < numDataPoints; i++) {
      mockData.push({
        time: now + i * 24 * 60 * 60, // roughly mock # of datapoints by adding i number of days
        values: { v2: Math.random() * 10e9, v3: Math.random() * 10e9 },
      } as StackedBarsData)
    }
    return mockData
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timePeriod])

  // Mock cumulative volumes data for chart header
  const setHeaderStatsToCumulative = useCallback(
    (timePeriod: TimePeriod) => {
      function timePeriodtoTimespan(timePeriod: TimePeriod) {
        switch (timePeriod) {
          case TimePeriod.DAY:
            return t`Past month`
          case TimePeriod.WEEK:
            return t`Past year`
          case TimePeriod.MONTH:
          default:
            return t`All time`
        }
      }
      setHeaderData({
        time: timePeriodtoTimespan(timePeriod),
        value: priceFormatter(Math.random() * 1e10),
      })
      setV2Volume('v2')
      setV3Volume('v3')
    },
    [priceFormatter]
  )

  const onCrosshairMove = useCallback(
    (data?: StackedBarsData) => {
      if (data === undefined) {
        setHeaderStatsToCumulative(timePeriod)
      } else {
        setHeaderData({
          time: headerDateFormatter(data.time),
          value: priceFormatter(getCumulativeSum(data)),
        })
        setV2Volume(priceFormatter(data.values.v2))
        setV3Volume(priceFormatter(data.values.v3))
      }
    },
    [headerDateFormatter, priceFormatter, setHeaderStatsToCumulative, timePeriod]
  )

  useEffect(() => setHeaderStatsToCumulative(timePeriod), [setHeaderStatsToCumulative, timePeriod])

  const params: StackedVolumeChartModelParams = useMemo(() => {
    return {
      data,
      locale,
      theme,
      colors: [theme.accent1, colors.blue400],
      format,
      onCrosshairMove,
    }
  }, [data, locale, theme, format, onCrosshairMove])

  return (
    <ChartContainer $height={height}>
      <StackedVolumeChartHeader headerData={headerData} />
      {!isSmallScreen && (
        <>
          <TimeSelectorContainer>
            <StyledTimePeriodSelector
              options={TIME_SELECTOR_OPTIONS}
              timePeriod={timePeriod}
              onChangeTimePeriod={setTimePeriod}
            />
          </TimeSelectorContainer>
          <Legend>
            <ProtocolLegend>
              <ThemedText.Caption>{v3Volume}</ThemedText.Caption>
              <LegendColoredSquare $color={theme.accent1} />
            </ProtocolLegend>
            <ProtocolLegend>
              <ThemedText.Caption>{v2Volume}</ThemedText.Caption>
              <LegendColoredSquare $color={colors.blue400} />
            </ProtocolLegend>
          </Legend>
          <Chart Model={StackedVolumeChartModel} params={params} height={height} />
        </>
      )}
    </ChartContainer>
  )
}
